/**
 * How much answer you want.
 *
 * Length is a real pedagogical choice, not a formatting preference: "what is
 * curl" deserves a different artefact when you're checking a definition than
 * when you're learning it for the first time. The reader picks; the tutor is
 * told the target explicitly, and the token ceiling is raised to match so a
 * full lecture is never cut off mid-derivation.
 *
 * The reader may also decline to pick, which is what `auto` is for — see
 * `DepthChoice` at the bottom of this file.
 */

export const DEPTH_IDS = ["brief", "standard", "deep", "lecture"] as const;

export type Depth = (typeof DEPTH_IDS)[number];

export const DEFAULT_DEPTH: Depth = "standard";

export interface DepthInfo {
  readonly id: Depth;
  readonly label: string;
  /** Shown in the picker. */
  readonly blurb: string;
  /** Target length, stated to the tutor in words. */
  readonly target: string;
  /** Output ceiling. Generous — running out mid-proof is the worst failure. */
  readonly maxTokens: number;
}

export const DEPTHS: readonly DepthInfo[] = [
  {
    id: "brief",
    label: "Brief",
    blurb: "A direct answer. For checking something you mostly know.",
    target: "roughly 150–350 words",
    maxTokens: 4_000,
  },
  {
    id: "standard",
    label: "Standard",
    blurb: "A full explanation with the derivation and the checks.",
    target: "roughly 700–1,400 words",
    maxTokens: 16_000,
  },
  {
    id: "deep",
    label: "Deep",
    blurb: "Everything from first principles, nothing assumed.",
    target: "roughly 2,000–4,000 words",
    maxTokens: 32_000,
  },
  {
    id: "lecture",
    label: "Lecture",
    blurb: "A complete set of lecture notes on the topic.",
    target:
      "a complete lecture — 5,000 words or more if the topic needs it, with sections, worked examples, and exercises",
    maxTokens: 64_000,
  },
] as const;

export const DEPTH_BY_ID: Readonly<Record<Depth, DepthInfo>> = Object.freeze(
  Object.fromEntries(DEPTHS.map((d) => [d.id, d])) as Record<Depth, DepthInfo>,
);

export function isDepth(value: unknown): value is Depth {
  return typeof value === "string" && (DEPTH_IDS as readonly string[]).includes(value);
}

/* -------------------------------------------------------------------------- */
/* What the reader asks for, which may be "you decide"                         */
/* -------------------------------------------------------------------------- */

/**
 * `Depth` is a *resolved* length: it indexes `DEPTH_BY_ID`, and every value has
 * a word target and a token ceiling. `auto` has neither — it is a request for
 * the planner to choose, resolved on the server before the tutor is called.
 *
 * Keeping them as two types is what stops `DEPTH_BY_ID["auto"]` from ever being
 * written; the compiler rejects it rather than the ceiling silently becoming
 * `undefined` and a lecture getting cut off in the middle of a derivation.
 */
export const DEPTH_CHOICE_IDS = ["auto", ...DEPTH_IDS] as const;

export type DepthChoice = (typeof DEPTH_CHOICE_IDS)[number];

export const DEFAULT_DEPTH_CHOICE: DepthChoice = "auto";

/** Deliberately not a `DepthInfo`: there is no target or ceiling to give. */
export const AUTO_DEPTH = {
  id: "auto",
  label: "Auto",
  blurb: "Let the planner judge the right length from your question.",
} as const;

export function isDepthChoice(value: unknown): value is DepthChoice {
  return (
    typeof value === "string" &&
    (DEPTH_CHOICE_IDS as readonly string[]).includes(value)
  );
}
